import type { ReactNode } from 'react';
import { Text } from '../atoms/Text';
import { useLinkComponent } from '../navigation';
import styles from './StatCard.module.css';

export interface StatCardProps {
  label: string;
  value: number | string;
  format?: (value: number) => string;
  /** Change compared to before, e.g. `{ value: '+12 %', direction: 'up' }`. */
  delta?: { value: string; direction: 'up' | 'down' | 'flat'; label?: string } | undefined;
  /** Grey line below the value, e.g. "Vorjahr: 85 kg". */
  footnote?: ReactNode;
  /** Makes the figure a link into its area. */
  href?: string;
}

/** A key figure: label, value, comparison. Plain text, no decoration (§6.13). */
export function StatCard({ label, value, format, delta, footnote, href }: StatCardProps) {
  const Link = useLinkComponent();
  const shown =
    typeof value === 'number' ? (format ? format(value) : value.toLocaleString()) : value;
  const body = (
    <>
      <Text as="span" size="sm" tone="muted">
        {label}
      </Text>
      <span className={styles.value}>{shown}</span>
      {(delta || footnote) && (
        <span className={styles.bottom}>
          {delta && (
            <span
              className={styles[delta.direction]}
              {...(delta.label ? { title: delta.label } : {})}
            >
              {delta.value}
            </span>
          )}
          {footnote && <span>{footnote}</span>}
        </span>
      )}
    </>
  );
  return href ? (
    <Link href={href} className={`${styles.stat} ${styles.link}`}>
      {body}
    </Link>
  ) : (
    <div className={styles.stat}>{body}</div>
  );
}
